import React, { useState } from 'react';
import { Shield, Search, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { licPlans } from '../lib/policiesData';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';

const LICPlans = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredPlans = licPlans.filter(plan => 
    plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (plan.nameTe && plan.nameTe.toLowerCase().includes(searchTerm.toLowerCase())) ||
    plan.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.shortDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (plan.shortDescTe && plan.shortDescTe.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const licSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': isTe ? 'భారతదేశంలో ఎల్‌ఐసి జీవిత బీమా పాలసీలు | VSR Insurance Plus' : 'LIC Life Insurance Plans in India | VSR Insurance Plus',
    'description': 'Explore complete LIC life insurance portfolio with senior advisor V. S. Reddy: Jeevan Utsav, Jeevan Shanti, Amrit Baal, Jeevan Anand, and pure term plans.',
    'provider': {
      '@type': 'InsuranceAgency',
      'name': 'VSR Insurance Plus'
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? "ఎల్‌ఐసి జీవిత బీమా & పెన్షన్ పాలసీలు | వి. ఎస్. రెడ్డి | VSR Insurance Plus" : "LIC Life Insurance Plans in India | V. S. Reddy | VSR Insurance Plus"}
        description={isTe ? "కేంద్ర ప్రభుత్వ హామీ గల ఎల్‌ఐసి పాలసీలు: జీవన్ ఉత్సవ్ (10% జీవితాంత ఆదాయం), న్యూ జీవన్ శాంతి పెన్షన్, అమృతబాల్ పిల్లల ఉన్నత విద్య మరియు ప్యూర్ టర్మ్ ప్లాన్లు. సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి." : "Browse sovereign-backed LIC policies: Jeevan Utsav (10% lifetime income), New Jeevan Shanti pension, Amrit Baal child education, and pure term plans. Free quote from V. S. Reddy."}
        canonicalPath="/lic-plans"
        schemaJson={licSchema}
        breadcrumbs={[{ name: isTe ? 'ఎల్‌ఐసి పాలసీలు' : 'LIC Plans', path: '/lic-plans' }]}
      />

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center justify-center p-2.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mb-4">
            <Shield className="w-6 h-6 text-yellow-600 dark:text-yellow-500" />
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
            {t('lic_life_insurance_plans')}
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
            {t('lic_plans_desc')} {isTe ? "25+ సంవత్సరాల అనుభవం గల సీనియర్ అడ్వైజర్ వి. ఎస్. రెడ్డి గారి వ్యక్తిగత మార్గదర్శకత్వం." : "Guided personally by Senior Advisor V. S. Reddy with 25+ years of verified industry experience."}
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-12 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input 
            type="text" 
            placeholder={t('search_lic_plans')} 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-6 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary text-slate-900 dark:text-white text-sm"
          />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredPlans.map(plan => (
            <div key={plan.id} className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
              <div>
                <span className="inline-block px-3 py-1 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4 border border-yellow-100 dark:border-yellow-900/30">
                  {isTe ? (plan.tagTe || plan.tag) : plan.tag}
                </span>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {isTe ? (plan.nameTe || plan.name) : plan.name}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-5 text-sm leading-relaxed">
                  {isTe ? (plan.shortDescTe || plan.shortDesc) : plan.shortDesc}
                </p>
                
                <div className="space-y-2 mb-6">
                  {(isTe && plan.featuresTe ? plan.featuresTe : plan.features).slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex gap-2 items-start text-xs text-slate-600 dark:text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between mt-auto">
                <div>
                  <div className="text-[11px] text-slate-400 font-medium">{t('cover_amount')}</div>
                  <div className="text-base font-bold text-slate-900 dark:text-white">
                    {isTe ? (plan.coverageTe || plan.coverage) : plan.coverage}
                  </div>
                </div>
                <Link to={`/plan/${plan.id}`} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors flex items-center gap-1.5 shadow-sm">
                  {t('view_plan')} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
        
        {filteredPlans.length === 0 && (
          <div className="text-center py-20 text-slate-500 text-base">
            {t('no_plans_found')}
          </div>
        )}
      </div>
    </div>
  );
};

export default LICPlans;
